import api from './api.service';
import type { DeliveryExecutionMode } from '@/utils/executionScope';

export interface DeliveryNoteItemPayload {
  sales_order_item_id: string;
  delivered_quantity: number;
  size?: string;
  weight?: number;
  weight_uom?: string;
  boxes?: number;
  hsn_code?: string;
  quantity_per_box?: number;
  notes?: string;
}

export interface CreateDeliveryPayload {
  sales_order_id: string;
  work_order_id?: string;
  delivery_date?: string;
  customer_po_number?: string;
  delivery_address?: string;
  notes?: string;
  country_of_origin?: string;
  warehouse_id?: string;
  items: DeliveryNoteItemPayload[];
}

export type DeliveryPackageType = 'box' | 'pallet';

export interface DeliveryNotePackage {
  id: string;
  company_id?: string;
  delivery_note_id?: string;
  line_number: number;
  package_type: DeliveryPackageType | string;
  quantity: number;
  size?: string | null;
  weight?: number | null;
  weight_uom?: string | null;
  remarks?: string | null;
}

export interface DeliveryPackageInput {
  package_type: DeliveryPackageType | string;
  quantity?: number;
  size?: string | null;
  weight?: number | null;
  weight_uom?: string | null;
  remarks?: string | null;
}

export interface DeliveryNoteItem {
  id: string;
  delivery_note_id: string;
  sales_order_item_id: string;
  line_number: number;
  item_code?: string;
  item_description?: string;
  delivered_quantity: number;
  uom?: string;
  size?: string;
  weight?: number;
  weight_uom?: string;
  boxes?: number;
  hsn_code?: string;
  quantity_per_box?: number;
  notes?: string;
  salesOrderItem?: { ordered_quantity: number; specifications?: string };
}

export interface DeliveryNote {
  id: string;
  company_id: string;
  delivery_note_number: string;
  sales_order_id: string;
  revenue_service_type_id?: string | null;
  customer_id: string;
  customer_po_number?: string;
  delivery_date: string;
  delivery_address?: string;
  shipping_method?: string;
  tracking_number?: string;
  delivery_status: string;
  vehicle_number?: string;
  driver_name?: string;
  delivery_assignee_user_id?: string;
  deliveryAssignee?: { id: string; first_name: string; last_name?: string; email?: string };
  in_transit_at?: string;
  delivered_complete_at?: string;
  returned_at?: string;
  status_transition_remarks?: string;
  received_by?: string;
  received_date?: string;
  notes?: string;
  special_instructions?: string;
  country_of_origin?: string | null;
  /** Source warehouse for stock issue on Delivered (Wave 0). */
  warehouse_id?: string | null;
  stock_posted?: boolean;
  stock_posted_at?: string | null;
  stock_journal_voucher_id?: string | null;
  document_package_status?: string | null;
  document_collection_request_id?: string | null;
  document_package_required?: boolean;
  /** User id of document creator (Prepared By). */
  created_by?: string | null;
  created_at: string;
  updated_at: string;
  salesOrder?: { sales_order_number: string; quotation_id?: string };
  customer?: { customer_name: string; address?: string };
  items?: DeliveryNoteItem[];
  packages?: DeliveryNotePackage[];
  /** Sales invoices created from this DN (line and/or header link). */
  sales_invoices?: Array<{ id: string; invoice_number: string; invoice_status?: string }>;
  sales_invoice_numbers?: string[];
  sales_invoice_number?: string;
}

export interface MyDeliveryAssignment {
  id: string;
  delivery_note_number: string;
  delivery_status: string;
  delivery_date: string;
  delivered_complete_at?: string | null;
  in_transit_at?: string | null;
  customer_name?: string | null;
  customer_phone?: string | null;
  customer_email?: string | null;
  delivery_address?: string | null;
  sales_order_number?: string | null;
  item_count: number;
  proof_count: number;
  special_instructions?: string | null;
  notes?: string | null;
}

/** Optional body for POST .../mark-in-transit | mark-delivered | mark-returned */
export interface DeliveryStatusTransitionBody {
  remarks?: string;
  tracking_number?: string;
  shipping_method?: string;
  delivered_at?: string;
  /** Set when marking in transit */
  vehicle_number?: string;
  driver_name?: string;
  delivery_assignee_user_id?: string;
}

/** Body for PATCH .../transit — edit shipment fields without status change */
export interface DeliveryTransitUpdateBody {
  remarks?: string;
  tracking_number?: string;
  shipping_method?: string;
  vehicle_number?: string;
  driver_name?: string;
  driver_phone?: string;
  delivery_assignee_user_id?: string;
  in_transit_at?: string;
}

/** Ant Design Tag color for canonical delivery note statuses */
export function getDeliveryStatusTagColor(status: string | undefined): string {
  const s = status || 'Prepared';
  if (s === 'Delivered') return 'green';
  if (s === 'In Transit') return 'blue';
  if (s === 'Returned') return 'red';
  return 'default';
}

export interface DeliveryPrintData {
  delivery_note_number: string;
  delivery_date: string;
  customer_po_number?: string;
  sap_number?: string | null;
  /** Resolved label for print (e.g. `MS — Manufacturing Services`). */
  revenue_service_type_label?: string;
  country_of_origin?: string | null;
  quotation_number: string;
  order_number: string;
  /** Sales order currency for commercial invoice print. */
  currency?: string;
  customer_name: string;
  customer_address: string;
  received_by?: string;
  received_date?: string;
  delivery_status?: string | null;
  tracking_number?: string | null;
  shipping_method?: string | null;
  vehicle_number?: string | null;
  driver_name?: string | null;
  driver_phone?: string | null;
  special_instructions?: string | null;
  notes?: string | null;
  status_transition_remarks?: string | null;
  in_transit_at?: string | null;
  delivered_complete_at?: string | null;
  returned_at?: string | null;
  delivery_assignee_name?: string | null;
  /** Username of the user who created the delivery note. */
  prepared_by?: string | null;
  created_by?: string | null;
  items: {
    line_number: number;
    item_code?: string | null;
    specification: string;
    qty_required: number;
    qty_delivered: number;
    uom?: string;
    /** SO unit price for commercial invoice (no tax). */
    unit_price?: number;
    /** Net line amount for delivered qty (no VAT). */
    line_amount?: number;
    /** Manufacturing unit number when line was shipped unit-wise. */
    production_unit_number?: number | null;
    size?: string;
    weight?: number;
    weight_uom?: string;
    boxes?: number;
    hsn_code?: string;
    quantity_per_box?: number;
  }[];
  packages?: Array<{
    line_number: number;
    package_type: string;
    quantity: number;
    size?: string | null;
    weight?: number | null;
    weight_uom?: string | null;
    remarks?: string | null;
  }>;
  totals: {
    total_qty_required: number;
    total_qty_delivered: number;
    /** Commercial invoice money totals (no tax). */
    subtotal?: number;
    grand_total?: number;
  };
}

export interface DeliveryListFilters {
  sales_order_id?: string;
  delivery_status?: string;
  date_from?: string;
  date_to?: string;
  customer_id?: string;
  search?: string;
}

export interface WorkOrderReadyLineItem {
  id: string;
  line_number: number;
  item_code: string | null;
  item_description: string;
  revision?: string | null;
  planned_quantity: number;
  produced_quantity: number;
  /** From API: max deliverable from completed WO stages (before prior deliveries). */
  manufacturing_deliverable_quantity?: number;
  sales_order_line_item_id: string | null;
  already_delivered: number;
  pending: number;
  uom: string;
  /** Completed manufacturing unit numbers when the line is unit-tracked (empty for bulk-only lines). */
  completed_production_units?: number[];
  delivery_execution_mode?: DeliveryExecutionMode;
}

export interface WorkOrderReadyForDelivery {
  id: string;
  work_order_number: string;
  sales_order_id: string;
  sales_order_number: string;
  customer_id: string;
  customer_name: string | null;
  work_order_status: string;
  order_date: string | null;
  release_quantity: number;
  already_delivered_wo: number;
  pending_wo: number;
  line_items: WorkOrderReadyLineItem[];
  execution_scope?: string | null;
  operation_generation_mode?: DeliveryExecutionMode;
  delivery_execution_mode?: DeliveryExecutionMode;
}

export interface EligibleDeliveryLineRow {
  work_order_id: string;
  work_order_number: string;
  sales_order_id: string;
  work_order_line_item_id: string;
  line_number: number;
  item_code: string | null;
  item_description: string;
  planned_quantity: number;
  manufacturing_deliverable_quantity: number;
  already_delivered: number;
  pending: number;
  completed_production_units: number[];
  execution_scope?: string | null;
  delivery_execution_mode?: DeliveryExecutionMode;
}

export interface DirectDeliveryEligibleLine {
  sales_order_item_id: string;
  line_number: number;
  item_code: string | null;
  item_description: string;
  pending: number;
  uom: string | null;
}

export interface DirectDeliveryEligibleSalesOrder {
  id: string;
  sales_order_number: string;
  customer_id: string;
  customer_name: string | null;
  line_items: DirectDeliveryEligibleLine[];
}

export interface CreateDeliveryFromWorkOrderPayload {
  work_order_id: string;
  delivery_date?: string;
  customer_po_number?: string;
  delivery_address?: string;
  notes?: string;
  items: {
    work_order_line_item_id: string;
    delivered_quantity: number;
    production_unit_number?: number | null;
    size?: string;
    weight?: number;
    weight_uom?: string;
    boxes?: number;
    hsn_code?: string;
    quantity_per_box?: number;
    notes?: string;
  }[];
}

export type DeliveryReadinessStatus = 'ready' | 'partial' | 'blocked' | 'delivered';

export interface DeliveryReadinessRow {
  row_key: string;
  readiness_status: DeliveryReadinessStatus;
  reasons: string[];
  customer_id: string | null;
  customer_name: string | null;
  project_id: string | null;
  project_name: string | null;
  sales_order_id: string | null;
  sales_order_number: string | null;
  work_order_id: string;
  work_order_number: string;
  work_order_line_item_id: string;
  line_number: number;
  item_code: string | null;
  item_description: string;
  execution_scope: string | null;
  delivery_execution_mode: 'unit_wise' | 'boq_line';
  production_unit_number: number | null;
  planned_quantity: number;
  manufacturing_deliverable_quantity: number;
  already_delivered: number;
  available_quantity: number;
  units_ready: number | null;
  units_planned: number | null;
  fir_status: string | null;
  target_delivery_date: string | null;
  delivery_location: string | null;
  priority: string | null;
  days_overdue: number | null;
  delivery_assignee_user_id: string | null;
  delivery_assignee_name: string | null;
  source_document_type: string | null;
}

export interface DeliveryReadinessBoardKpis {
  readyToday: number;
  readyUnits: number;
  blockedCount: number;
  pendingDispatches: number;
  todaysDeliveryNotes: number;
  overdueDeliveries: number;
}

export interface DeliveryReadinessBoard {
  kpis: DeliveryReadinessBoardKpis;
  ready: DeliveryReadinessRow[];
  partial: DeliveryReadinessRow[];
  blocked: DeliveryReadinessRow[];
}

export interface DeliveryReadinessBoardFilters {
  customer_id?: string;
  sales_order_id?: string;
  work_order_id?: string;
  project_id?: string;
  execution_scope?: string;
  date_from?: string;
  date_to?: string;
  /** Free-text across customer, SO #, WO #, item, project. */
  search?: string;
}

export interface CreateDeliveryFromWorkOrderBatchPayload {
  sales_order_id: string;
  delivery_date?: string;
  customer_po_number?: string;
  delivery_address?: string;
  notes?: string;
  items: Array<{
    work_order_id: string;
    work_order_line_item_id: string;
    delivered_quantity: number;
    production_unit_number?: number | null;
    size?: string;
    weight?: number;
    weight_uom?: string;
    boxes?: number;
    hsn_code?: string;
    quantity_per_box?: number;
    notes?: string;
  }>;
}

class DeliveryService {
  async list(filters?: DeliveryListFilters): Promise<DeliveryNote[]> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, String(value));
      });
    }
    const response = await api.get<DeliveryNote[]>(`/sales/deliveries?${params.toString()}`);
    const res = response as { data?: DeliveryNote[] };
    if (Array.isArray(res.data)) return res.data;
    return [];
  }

  async getById(id: string): Promise<DeliveryNote> {
    const response = await api.get<DeliveryNote>(`/sales/deliveries/${id}`);
    const res = response as { data?: DeliveryNote };
    if (res.data) return res.data;
    throw new Error('Delivery not found');
  }

  async getPrintData(id: string): Promise<DeliveryPrintData> {
    const response = await api.get<DeliveryPrintData>(`/sales/deliveries/${id}/print`);
    const res = response as { data?: DeliveryPrintData };
    if (res.data) return res.data;
    throw new Error('Print data not found');
  }

  async create(payload: CreateDeliveryPayload): Promise<DeliveryNote> {
    const response = await api.post<DeliveryNote>('/sales/deliveries', payload);
    const res = response as { data?: DeliveryNote };
    if (res.data) return res.data;
    throw new Error('Create delivery failed');
  }

  async getEligibleWorkOrders(customerId?: string): Promise<WorkOrderReadyForDelivery[]> {
    const params = new URLSearchParams();
    if (customerId) params.append('customer_id', customerId);
    const response = await api.get<WorkOrderReadyForDelivery[]>(`/sales/deliveries/eligible-work-orders?${params.toString()}`);
    const body = response as { data?: WorkOrderReadyForDelivery[] | { data?: WorkOrderReadyForDelivery[] } };
    const d = body.data;
    const list = Array.isArray(d) ? d : d && typeof d === 'object' && Array.isArray((d as { data?: WorkOrderReadyForDelivery[] }).data) ? (d as { data: WorkOrderReadyForDelivery[] }).data : [];
    return Array.isArray(list) ? list : [];
  }

  async getEligibleDeliveryLines(salesOrderId: string): Promise<EligibleDeliveryLineRow[]> {
    const params = new URLSearchParams({ sales_order_id: salesOrderId });
    const response = await api.get<EligibleDeliveryLineRow[]>(`/sales/deliveries/eligible-lines?${params.toString()}`);
    const body = response as { data?: EligibleDeliveryLineRow[] };
    return Array.isArray(body.data) ? body.data : [];
  }

  async createFromWorkOrder(payload: CreateDeliveryFromWorkOrderPayload): Promise<DeliveryNote> {
    const response = await api.post<DeliveryNote>('/sales/deliveries/from-work-order', payload);
    const res = response as { data?: DeliveryNote };
    if (res.data) return res.data;
    throw new Error('Create delivery from work order failed');
  }

  async getDeliveryReadinessBoard(filters?: DeliveryReadinessBoardFilters): Promise<DeliveryReadinessBoard> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, String(value));
      });
    }
    const qs = params.toString();
    const response = await api.get<DeliveryReadinessBoard>(
      `/sales/delivery-readiness/board${qs ? `?${qs}` : ''}`
    );
    const res = response as { data?: DeliveryReadinessBoard };
    if (res.data) return res.data;
    return {
      kpis: {
        readyToday: 0,
        readyUnits: 0,
        blockedCount: 0,
        pendingDispatches: 0,
        todaysDeliveryNotes: 0,
        overdueDeliveries: 0,
      },
      ready: [],
      partial: [],
      blocked: [],
    };
  }

  async createFromWorkOrderBatch(payload: CreateDeliveryFromWorkOrderBatchPayload): Promise<DeliveryNote> {
    const response = await api.post<DeliveryNote>('/sales/deliveries/from-work-order-batch', payload);
    const res = response as { data?: DeliveryNote };
    if (res.data) return res.data;
    throw new Error('Create delivery from work orders failed');
  }

  async isEligibleForDelivery(salesOrderId: string): Promise<{
    eligible: boolean;
    requires_work_order_flow?: boolean;
    can_deliver_direct?: boolean;
  }> {
    const response = await api.get(`/sales/orders/${salesOrderId}/eligible-for-delivery`);
    const res = response as {
      data?: { eligible: boolean; requires_work_order_flow?: boolean; can_deliver_direct?: boolean };
    };
    return res.data ?? { eligible: false };
  }

  async getEligibleSalesOrdersForDirect(customerId?: string): Promise<DirectDeliveryEligibleSalesOrder[]> {
    const params = new URLSearchParams();
    if (customerId) params.append('customer_id', customerId);
    const response = await api.get<DirectDeliveryEligibleSalesOrder[]>(
      `/sales/deliveries/eligible-sales-orders-direct?${params.toString()}`
    );
    const body = response as { data?: DirectDeliveryEligibleSalesOrder[] };
    return Array.isArray(body.data) ? body.data : [];
  }

  /** Create a delivery note for an order that was marked Delivered but has no delivery note yet. */
  async createRetrospective(salesOrderId: string): Promise<DeliveryNote> {
    const response = await api.post<DeliveryNote>('/sales/deliveries/retrospective', { sales_order_id: salesOrderId });
    const res = response as { data?: DeliveryNote };
    if (res.data) return res.data;
    throw new Error('Create retrospective delivery failed');
  }

  /** Update packing fields for a delivery note item. */
  async updateItemPacking(
    deliveryId: string,
    itemId: string,
    body: { size?: string; weight?: number; weight_uom?: string; boxes?: number; hsn_code?: string }
  ): Promise<DeliveryNoteItem> {
    const response = await api.patch<DeliveryNoteItem>(
      `/sales/deliveries/${deliveryId}/items/${itemId}`,
      body
    );
    const res = response as { data?: DeliveryNoteItem };
    if (res.data) return res.data;
    throw new Error('Update packing failed');
  }

  async updateCountryOfOrigin(
    deliveryId: string,
    country_of_origin: string | null
  ): Promise<DeliveryNote> {
    const response = await api.patch<DeliveryNote>(
      `/sales/deliveries/${deliveryId}/country-of-origin`,
      { country_of_origin }
    );
    const res = response as { data?: DeliveryNote };
    if (res.data) return res.data;
    throw new Error('Update country of origin failed');
  }

  async updateWarehouse(deliveryId: string, warehouse_id: string): Promise<DeliveryNote> {
    const response = await api.patch<DeliveryNote>(`/sales/deliveries/${deliveryId}/warehouse`, {
      warehouse_id,
    });
    const res = response as { data?: DeliveryNote };
    if (res.data) return res.data;
    throw new Error('Update warehouse failed');
  }

  async replacePackages(deliveryId: string, packages: DeliveryPackageInput[]): Promise<DeliveryNote> {
    const response = await api.put<DeliveryNote>(`/sales/deliveries/${deliveryId}/packages`, {
      packages,
    });
    const res = response as { data?: DeliveryNote };
    if (res.data) return res.data;
    throw new Error('Update packages failed');
  }

  async markInTransit(id: string, body?: DeliveryStatusTransitionBody): Promise<DeliveryNote> {
    const response = await api.post<DeliveryNote>(`/sales/deliveries/${id}/mark-in-transit`, body ?? {});
    const res = response as { data?: DeliveryNote };
    if (res.data) return res.data;
    throw new Error('Mark in transit failed');
  }

  async updateTransitDetails(id: string, body: DeliveryTransitUpdateBody): Promise<DeliveryNote> {
    const response = await api.patch<DeliveryNote>(`/sales/deliveries/${id}/transit`, body);
    const res = response as { data?: DeliveryNote };
    if (res.data) return res.data;
    throw new Error('Update transit details failed');
  }

  async markDelivered(id: string, body?: DeliveryStatusTransitionBody): Promise<DeliveryNote> {
    const response = await api.post<DeliveryNote>(`/sales/deliveries/${id}/mark-delivered`, body ?? {});
    const res = response as { data?: DeliveryNote };
    if (res.data) return res.data;
    throw new Error('Mark delivered failed');
  }

  async listMyAssignments(): Promise<MyDeliveryAssignment[]> {
    const response = await api.get<MyDeliveryAssignment[]>('/sales/deliveries/my-assignments');
    const res = response as { data?: MyDeliveryAssignment[] };
    return Array.isArray(res.data) ? res.data : [];
  }

  async markReturned(id: string, body?: DeliveryStatusTransitionBody): Promise<DeliveryNote> {
    const response = await api.post<DeliveryNote>(`/sales/deliveries/${id}/mark-returned`, body ?? {});
    const res = response as { data?: DeliveryNote };
    if (res.data) return res.data;
    throw new Error('Mark returned failed');
  }
}

const deliveryService = new DeliveryService();
export default deliveryService;
